import { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Badge,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useOriginRewrite,
  useUpdateOriginRewrite,
  useDeleteOriginRewrite,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';
import { CdnAspectDrift } from '../_shared/VendorDrift';

interface OriginHeader {
  name: string;
  value: string;
}
interface RewriteRule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  match_type: string;
  match_path: string;
  urls: string[];
  weights: number[];
  selector: string;
  host_header: string;
  tls_to_origin: boolean;
  sni_override: string;
  protocol: string;
  origin_headers: OriginHeader[];
  origin_tls_verify: boolean;
}

const MATCH_TYPES = [
  { value: 'prefix', label: 'Prefix' },
  { value: 'exact', label: 'Exact' },
  { value: 'suffix', label: 'Suffix' },
  { value: 'contains', label: 'Contains' },
  { value: 'wildcard', label: 'Wildcard (*/?)' },
  { value: 'regex', label: 'Regex' },
];
const PROTOCOLS = [
  { value: 'h1.1', label: 'HTTP/1.1' },
  { value: 'h2', label: 'HTTP/2' },
];
const SELECTORS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'failover', label: 'Failover' },
  { value: 'weighted_round_robin', label: 'Weighted' },
];

let _id = 0;
const localId = () => `r${++_id}`;

const emptyRule = (priority: number): RewriteRule => ({
  id: localId(),
  name: '',
  priority,
  enabled: true,
  match_type: 'prefix',
  match_path: '',
  urls: [''],
  weights: [1],
  selector: 'round_robin',
  host_header: '',
  tls_to_origin: false,
  sni_override: '',
  protocol: 'h1.1',
  origin_headers: [],
  origin_tls_verify: true,
});

function hydrate(raw: Record<string, unknown>): RewriteRule {
  const r = raw as Record<string, any>;
  const urls: string[] = Array.isArray(r.urls) && r.urls.length ? r.urls : [''];
  const weights: number[] =
    Array.isArray(r.weights) && r.weights.length === urls.length ? r.weights : urls.map(() => 1);
  return {
    id: localId(),
    name: r.name ?? '',
    priority: r.priority ?? 100,
    enabled: r.enabled ?? true,
    match_type: r.match_type ?? 'prefix',
    match_path: r.match_path ?? '',
    urls,
    weights,
    selector: r.selector ?? 'round_robin',
    host_header: r.host_header ?? '',
    tls_to_origin: r.tls_to_origin ?? false,
    sni_override: r.sni_override ?? '',
    protocol: r.protocol ?? 'h1.1',
    origin_headers: Array.isArray(r.origin_headers)
      ? r.origin_headers.map((h: any) => ({ name: h.name ?? '', value: h.value ?? '' }))
      : [],
    origin_tls_verify: r.origin_tls_verify ?? true,
  };
}

function toPayload(r: RewriteRule): Record<string, unknown> {
  const finalUrls: string[] = [];
  const finalWeights: number[] = [];
  r.urls.forEach((u, i) => {
    if (u?.trim()) {
      finalUrls.push(u);
      finalWeights.push(Number(r.weights[i] ?? 1));
    }
  });
  return {
    name: r.name.trim(),
    priority: Number(r.priority),
    enabled: r.enabled,
    match_type: r.match_type,
    match_path: r.match_path,
    urls: finalUrls,
    weights: finalWeights,
    selector: r.selector,
    host_header: r.host_header,
    tls_to_origin: r.tls_to_origin,
    sni_override: r.sni_override,
    protocol: r.protocol,
    origin_headers: r.origin_headers.filter((h) => h.name.trim() !== ''),
    origin_tls_verify: r.origin_tls_verify,
  };
}

export function OriginRewriteTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useOriginRewrite(domainId);
  const isConfigured = !!cfg;

  const [masterEnabled, setMasterEnabled] = useState(false);
  const [rules, setRules] = useState<RewriteRule[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<RewriteRule | null>(null);

  useEffect(() => {
    if (!cfg) {
      setMasterEnabled(false);
      setRules([]);
      return;
    }
    setMasterEnabled(cfg.enabled);
    setRules(cfg.rules.map(hydrate).sort((a, b) => a.priority - b.priority));
  }, [cfg]);

  const update = useUpdateOriginRewrite(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'origin_rule', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Origin rewrite saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save origin rewrite', description: errorMessage(e, 'Could not save origin rewrite') }),
  });
  const remove = useDeleteOriginRewrite(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'origin_rule', 'delete');
      show({ tone: 'success', title: 'Origin rewrite removed' });
      setRules([]);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove origin rewrite', description: errorMessage(e, 'Could not remove origin rewrite') }),
  });
  const busy = update.isPending || remove.isPending;

  const patchRule = (id: string, changes: Partial<RewriteRule>) =>
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));

  const addRule = () => {
    const nextPriority = rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 100;
    const r = emptyRule(nextPriority);
    setRules((prev) => [...prev, r]);
    setExpanded((prev) => new Set(prev).add(r.id));
  };

  const save = () => {
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r.name.trim()) return show({ tone: 'danger', title: `Rule #${i + 1}: name is required` });
      if (!r.urls.some((u) => u.trim()))
        return show({ tone: 'danger', title: `Rule #${i + 1}: at least one origin URL is required` });
    }
    update.mutate({ enabled: masterEnabled, rules: rules.map(toPayload) });
  };

  if (isLoading) {
    return (
      <Section title="Origin Rewrite">
        <Skeleton style={{ height: 240 }} />
      </Section>
    );
  }

  return (
    <Section
      title="Origin Rewrite"
      description={`Route URL paths to different origins for ${domain}.`}
      actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="origin-rewrite" />
        <Card>
          <CardHeader
            actions={
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} disabled={busy} onClick={addRule}>
                Add rule
              </Button>
            }
          >
            <CardTitle>Rewrite rules</CardTitle>
            <CardDescription>
              {rules.length} rule{rules.length !== 1 ? 's' : ''} defined. Lower priority wins on match.
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title="Enabled"
              description="Master switch for path rewriting on this domain."
              control={<Switch checked={masterEnabled} onCheckedChange={setMasterEnabled} />}
            />
            {rules.length === 0 ? (
              <EmptyState size="sm" icon={<ArrowRightLeft size={20} />} title="No rewrite rules yet" description='Click "Add rule" to create one.' />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {rules.map((rule) => (
                  <RuleItem
                    key={rule.id}
                    rule={rule}
                    expanded={expanded.has(rule.id)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        next.has(rule.id) ? next.delete(rule.id) : next.add(rule.id);
                        return next;
                      })
                    }
                    onChange={(c) => patchRule(rule.id, c)}
                    onDelete={() => setPendingDelete(rule)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <Button disabled={busy} onClick={save}>
              {update.isPending ? 'Saving…' : isConfigured ? 'Save' : 'Create'}
            </Button>
          }
        />
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete rewrite rule?"
        description={`This removes ${pendingDelete?.name ? `“${pendingDelete.name}”` : 'this rule'} and its routing. Save to persist.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) setRules((prev) => prev.filter((r) => r.id !== pendingDelete.id));
          setPendingDelete(null);
        }}
      />
    </Section>
  );
}

const grid2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
};

function RuleItem({
  rule,
  expanded,
  onToggle,
  onChange,
  onDelete,
}: {
  rule: RewriteRule;
  expanded: boolean;
  onToggle: () => void;
  onChange: (changes: Partial<RewriteRule>) => void;
  onDelete: () => void;
}) {
  const setUrl = (idx: number, value: string) => {
    const urls = [...rule.urls];
    urls[idx] = value;
    onChange({ urls });
  };
  const setWeight = (idx: number, value: string) => {
    const weights = [...rule.weights];
    weights[idx] = Number(value) || 0;
    onChange({ weights });
  };
  const addUrl = () => onChange({ urls: [...rule.urls, ''], weights: [...rule.weights, 1] });
  const removeUrl = (idx: number) =>
    onChange({ urls: rule.urls.filter((_, i) => i !== idx), weights: rule.weights.filter((_, i) => i !== idx) });

  const addHeader = () => onChange({ origin_headers: [...rule.origin_headers, { name: '', value: '' }] });
  const setHeader = (idx: number, key: 'name' | 'value', value: string) => {
    const origin_headers = rule.origin_headers.map((h, i) => (i === idx ? { ...h, [key]: value } : h));
    onChange({ origin_headers });
  };
  const removeHeader = (idx: number) =>
    onChange({ origin_headers: rule.origin_headers.filter((_, i) => i !== idx) });

  const weighted = rule.selector === 'weighted_round_robin';

  return (
    <div style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
        <Switch checked={rule.enabled} onCheckedChange={(v) => onChange({ enabled: v })} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-lg)' }}>{rule.name || 'Untitled rule'}</span>
          <Badge tone="neutral" appearance="subtle" size="sm">
            priority {rule.priority}
          </Badge>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {rule.urls.filter((u) => u.trim()).length} origin{rule.urls.filter((u) => u.trim()).length !== 1 ? 's' : ''}
          </span>
        </div>
        <IconButton variant="ghost" size="sm" aria-label="Delete rule" onClick={onDelete}>
          <Trash2 size={15} />
        </IconButton>
        <IconButton variant="ghost" size="sm" aria-label="Toggle rule" onClick={onToggle}>
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </IconButton>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--vn-border-default)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={grid2}>
            <Field label="Name">
              {(p) => <Input {...p} value={rule.name} onChange={(e) => onChange({ name: e.target.value })} />}
            </Field>
            <Field label="Priority">
              {(p) => (
                <Input {...p} type="number" value={String(rule.priority)} onChange={(e) => onChange({ priority: Number(e.target.value) || 0 })} />
              )}
            </Field>
            <Field label="Match type">
              {(p) => (
                <Select value={rule.match_type} onValueChange={(v) => onChange({ match_type: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATCH_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Match path">
              {(p) => <Input {...p} value={rule.match_path} placeholder="/api" onChange={(e) => onChange({ match_path: e.target.value })} />}
            </Field>
          </div>

          <div>
            <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Origin URLs</div>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rule.urls.map((u, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                  <Input value={u} placeholder="https://origin.example.com" onChange={(e) => setUrl(idx, e.target.value)} style={{ flex: 1 }} />
                  {weighted && (
                    <Input
                      type="number"
                      value={String(rule.weights[idx] ?? 1)}
                      onChange={(e) => setWeight(idx, e.target.value)}
                      style={{ width: 90 }}
                      aria-label="Weight"
                    />
                  )}
                  {rule.urls.length > 1 && (
                    <IconButton variant="ghost" size="sm" aria-label="Remove URL" onClick={() => removeUrl(idx)}>
                      <Trash2 size={15} />
                    </IconButton>
                  )}
                </div>
              ))}
              <div>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addUrl}>
                  Add origin
                </Button>
              </div>
            </div>
          </div>

          <div style={grid2}>
            <Field label="Load balancing">
              {(p) => (
                <Select value={rule.selector} onValueChange={(v) => onChange({ selector: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SELECTORS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Protocol">
              {(p) => (
                <Select value={rule.protocol} onValueChange={(v) => onChange({ protocol: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROTOCOLS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Host header">
              {(p) => <Input {...p} value={rule.host_header} onChange={(e) => onChange({ host_header: e.target.value })} />}
            </Field>
            <Field label="SNI override">
              {(p) => <Input {...p} value={rule.sni_override} onChange={(e) => onChange({ sni_override: e.target.value })} />}
            </Field>
          </div>

          <SettingRow
            title="TLS to origin"
            control={<Switch checked={rule.tls_to_origin} onCheckedChange={(v) => onChange({ tls_to_origin: v })} />}
          />
          <SettingRow
            title="Verify origin certificate"
            control={<Switch checked={rule.origin_tls_verify} onCheckedChange={(v) => onChange({ origin_tls_verify: v })} />}
          />

          <div>
            <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Custom headers to origin</div>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rule.origin_headers.map((h, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                  <Input value={h.name} placeholder="Header name" onChange={(e) => setHeader(idx, 'name', e.target.value)} style={{ flex: 1 }} />
                  <Input value={h.value} placeholder="Header value" onChange={(e) => setHeader(idx, 'value', e.target.value)} style={{ flex: 2 }} />
                  <IconButton variant="ghost" size="sm" aria-label="Remove header" onClick={() => removeHeader(idx)}>
                    <Trash2 size={15} />
                  </IconButton>
                </div>
              ))}
              <div>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addHeader}>
                  Add header
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
